/* journey-extra.js — extra first-party journey events on top of journey-collect.js.
 *
 * Implements CHECKOUT-PATH-THEME.md + the delegated half of ENGAGEMENT-SCORE-THEME.md:
 *   • view/product (with URL, so the dashboard can derive "second product")
 *   • checkout/express/<method>  — Shop Pay / Apple Pay / Google Pay / PayPal (buttons that skip the cart)
 *   • checkout/standard          — the normal "Checkout" button (cart page / drawer)
 *   • checkout/add_to_cart       — a real add-to-cart on a NON-builder product form (the builder fires
 *                                  its own add_to_cart from bb-box.js, so we skip #bb-product-form here)
 *   • click/reviews              — the reviews block/widget is opened/clicked
 *   • click/delivery             — the delivery / shipping info block is opened/clicked
 *
 * All calls are safe no-ops if journey-collect.js hasn't loaded (window.bbTrack &&). Coarse UI labels
 * only — never any PII / personalisation content.
 */
(function () {
  function fire(type, name, value) { try { if (window.bbTrack) window.bbTrack(type, name, value); } catch (e) {} }
  var once = {};
  function onceFire(key, type, name, value) { if (once[key]) return; once[key] = 1; fire(type, name, value); }

  // ── CRM ingest (email-known customer events → the CRM/Email module) ───────────────────────
  // Posts through the SIGNED, same-origin App Proxy (/apps/barnaby/crm-ingest → crm-ingest.js). Shopify
  // signs the request and crm-ingest verifies the signature, so there is NO ingest token in the theme.
  // Same-origin also means application/json triggers no CORS preflight (the old text/plain + body-token
  // workaround for the aws-lambda-compat OPTIONS 502 is no longer needed). Fire ONLY where the email is
  // known (login, checkout, personalisation finish) — anonymous behaviour is journey-collect's job.
  // Valid event_name registry: product.viewed, collection.viewed, search.performed, cart.created,
  //   cart.updated, checkout.started, checkout.abandoned, story.started/completed/abandoned,
  //   personalisation.started/completed, quiz.started/completed, referral.completed,
  //   profile.identified, consent.granted/withdrawn. (Anything else quarantines server-side.)
  window.bbCrm = function (eventName, extra) {
    try {
      var email = (window.bbCustomer && window.bbCustomer.email) || (extra && extra.email) || '';
      if (!email) return;   // email-known moments only; no client secret, no anonymous sends
      var body = JSON.stringify({
        event_name: eventName,
        email: email,
        occurred_at: new Date().toISOString(),
        properties: (extra && extra.properties) || {}
      });
      fetch('/apps/barnaby/crm-ingest', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
    } catch (e) {}
  };

  // profile.identified — the moment we know who this is: a logged-in customer (once per session)…
  try {
    if (window.bbCustomer && window.bbCustomer.email) {
      if (!sessionStorage.getItem('bb_crm_identified')) { sessionStorage.setItem('bb_crm_identified', '1'); window.bbCrm('profile.identified'); }
    } else {
      // …or resolved slightly later by customer-recognition.liquid.
      document.addEventListener('bb:customer-resolved', function () {
        if (window.bbCustomer && window.bbCustomer.email && !sessionStorage.getItem('bb_crm_identified')) {
          try { sessionStorage.setItem('bb_crm_identified', '1'); } catch (e) {}
          window.bbCrm('profile.identified');
        }
      });
    }
  } catch (e) {}

  // …or an email typed into a newsletter / account signup form (delegated submit).
  document.addEventListener('submit', function (e) {
    var f = e.target; if (!f || f.nodeName !== 'FORM') return;
    var el = f.querySelector('input[type="email"], input[name*="[email]" i], input[name="email" i]');
    var em = el && (el.value || '').trim();
    if (em && em.indexOf('@') > 0) window.bbCrm('profile.identified', { email: em });
  }, true);

  // ── Product page view WITH the URL (engagement "second product" needs the path) ──
  try { if (location.pathname.indexOf('/products/') === 0) fire('view', 'product', location.pathname); } catch (e) {}

  // ── Real add-to-cart on a standard product form (the builder handles its own via bb-box.js) ──
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.nodeName !== 'FORM') return;
    var act = f.getAttribute('action') || '';
    if (act.indexOf('/cart/add') === -1) return;
    if (f.id === 'bb-product-form') return;   // builder: bb-box.js fires add_to_cart on the real add
    // Same dedupe window as bb-box (__bbA2Cts) so one physical add can never double-report; also feed
    // the funnel's CART stage for standard (non-builder) product adds.
    var _a2cNow = Date.now();
    if (window.__bbA2Cts && _a2cNow - window.__bbA2Cts <= 1500) return;
    window.__bbA2Cts = _a2cNow;
    fire('checkout', 'add_to_cart');
    fire('cart', 'standard');
  }, true);

  // ── Delegated click listener: checkout path (express/standard) + reviews / delivery opens ──
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    // Express / dynamic / accelerated checkout buttons (Shop Pay + wallets skip the cart).
    var exp = t.closest('.shopify-payment-button, .shopify-payment-button__button, [data-shopify="payment-button"], shopify-accelerated-checkout, shopify-accelerated-checkout-cart, [class*="accelerated" i], .wallet-button, [aria-label*="Pay" i]');
    if (exp) {
      var host = t.closest('button, [role="button"], [aria-label]') || exp;
      var lbl = (((host.getAttribute && host.getAttribute('aria-label')) || '') + ' ' + (host.textContent || '') + ' ' + (exp.className || '')).toLowerCase();
      var method = lbl.indexOf('shop') > -1 ? 'shop_pay'
        : lbl.indexOf('apple') > -1 ? 'apple_pay'
        : lbl.indexOf('google') > -1 ? 'google_pay'
        : lbl.indexOf('paypal') > -1 ? 'paypal' : 'express';
      fire('checkout', 'express', method);
      return;
    }

    // Normal Checkout button (cart page / drawer).
    if (t.closest('[name="checkout"], .cart__checkout, a[href="/checkout"], a[href^="/checkout"]')) {
      fire('checkout', 'standard');
      bbCartSnapshot();   // last cart state before checkout (keepalive completes during nav)
      // CRM: checkout.started (feeds abandon-checkout journeys). Only useful with an email — known
      // for logged-in customers here; guest email is entered on the hosted checkout page (a checkout
      // extension would be needed for that). The helper quarantines emailless sends.
      if (window.bbCustomer && window.bbCustomer.email) window.bbCrm('checkout.started');
      return;
    }

    // Reviews opened (widget / accordion / anchor / the trust "reviews" block). Coarse: any element
    // whose href/class/id mentions "review". The dashboard matches on the name substring "review".
    if (t.closest('[href*="review" i], [class*="review" i], [id*="review" i], [data-review], [aria-controls*="review" i]')) {
      onceFire('reviews', 'click', 'reviews');
      return;
    }

    // Delivery / shipping info opened (accordion / info link / the delivery trust block).
    if (t.closest('[class*="deliver" i], [id*="deliver" i], [href*="deliver" i], [class*="shipping" i], [id*="shipping" i], [aria-controls*="deliver" i]')) {
      onceFire('delivery', 'click', 'delivery');
      return;
    }
  }, true);

  // ── Cart snapshot (pre-checkout, cart-level abandonment) ─────────────────────────────────
  // Shopify's abandoned-checkout report only fires AFTER checkout starts (email entered); this
  // captures the cart BEFORE checkout, keyed by bb_vid, so the dashboard can measure personalised
  // carts that never buy. Sends NO personalisation content — only title, qty, a personalised flag and
  // a coarse format (date/name/story/plain). See CART-SNAPSHOT-THEME.md. order-webhook flips it to
  // converted on purchase (via bb_vid). Fires on cart-page load (debounced) + on checkout click.
  var SNAP_PERSO_KEYS = ['_add your story', '_names', '_dates', '_chapters', '_personalisation_id', '_name', '_top line', '_bottom line', '_step 1'];
  function snapIsPerso(keys) { return keys.some(function (k) { return SNAP_PERSO_KEYS.indexOf(k) > -1; }); }
  function snapFormat(props) {
    var m = {}; Object.keys(props || {}).forEach(function (k) { m[String(k).toLowerCase()] = props[k]; });
    var story = String(m['_add your story'] || '').toLowerCase();
    if (m['_dates'] || story.indexOf('date') > -1 || story.indexOf('remember') > -1) return 'date';
    if (m['_names'] || m['_name']) return 'name';
    if (m['_chapters'] || story.indexOf('story') > -1 || story.indexOf('chapter') > -1) return 'story';
    return 'plain';
  }
  var _snapT;
  function bbCartSnapshot() {
    if (!marketingAllowed()) return;
    var vid = ''; try { vid = localStorage.getItem('bb_vid') || ''; } catch (e) {}
    if (!vid) return;
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) return;
      var items = cart.items.slice(0, 40).map(function (li) {
        var keys = Object.keys(li.properties || {}).map(function (k) { return String(k).toLowerCase(); });
        return { title: String(li.product_title || li.title || '').slice(0, 80), qty: li.quantity || 0,
                 personalised: snapIsPerso(keys), format: snapFormat(li.properties) };
      });
      fetch('https://barnabydashboard.netlify.app/.netlify/functions/cart-snapshot', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ vid: vid, cart: {
          items: items,
          value: (cart.total_price || 0) / 100,
          hasPersonalisation: items.some(function (i) { return i.personalised; })
        } })
      }).catch(function () {});
    }).catch(function () {});
  }
  function bbCartSnapshotDebounced() { clearTimeout(_snapT); _snapT = setTimeout(bbCartSnapshot, 600); }
  try { if (location.pathname.indexOf('/cart') === 0) bbCartSnapshotDebounced(); } catch (e) {}

  // ── Durable first-touch attribution (carousel → Google, long return window) ──────────────
  // The first-touch cookie must be set SERVER-side or Safari/iOS ITP caps it to 7 days and a
  // 3-weeks-later Google return loses the carousel seed. We fire a 1×1 pixel at the App Proxy
  // (/apps/barnaby/track → the track.js function), which sets _ad_first (first-touch-wins, 180d)
  // and _ad_last (always) as first-party cookies that ITP does NOT truncate. We then copy those
  // cookies onto the cart so they land on the order (order-webhook stitches them).
  // See AD-FIRST-TOUCH-SERVER-COOKIE (this doc) + FIRST-LAST-TOUCH-ATTRIBUTION-THEME.md.
  var TRACK_PROXY = '/apps/barnaby/track';   // reuses the existing apps/barnaby App Proxy (same as last-box)
  var TOUCH_PARAMS = ['ad', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'msclkid', 'ttclid'];

  function readCookie(name) {
    var m = document.cookie.match('(?:^|;\\s*)' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }
  // Respect the storefront's consent where the Customer Privacy API is present; otherwise behave
  // like the existing collectors (journey-collect / _ft_ stamping both fire) and proceed.
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp) {
        if (typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed() || (typeof cp.marketingAllowed === 'function' && !!cp.marketingAllowed());
        if (typeof cp.marketingAllowed === 'function') return !!cp.marketingAllowed();
      }
    } catch (e) {}
    return true;
  }

  // Copy the durable _ad_first/_ad_last cookies onto the cart as note attributes (idempotent per
  // value — re-stamps only when the last-touch changes, so it costs one /cart/update per new touch).
  function stampAdTouch() {
    var first = readCookie('_ad_first'), last = readCookie('_ad_last');
    if (!first && !last) return;
    var sig = first + '¦' + last;
    try { if (sessionStorage.getItem('bb_ad_touch_sig') === sig) return; } catch (e) {}
    // Cart-race rule (2026-08-25): attributes never mint a cart; serialised through the chain.
    var _stampTouch = function () {
      (window.bbQueueCartWrite ? window.bbQueueCartWrite({ _ad_first: first, _ad_last: last })
        : fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _ad_first: first, _ad_last: last } }) })
      ).then(function () { try { sessionStorage.setItem('bb_ad_touch_sig', sig); } catch (e) {} }).catch(function () {});
    };
    if (window.bbCartExists && !window.bbCartExists()) { document.addEventListener('bb:cart-added', _stampTouch, { once: true }); }
    else { _stampTouch(); }
  }

  (function firstTouch() {
    var q;
    try { q = new URLSearchParams(location.search); } catch (e) { q = null; }
    var parts = [];
    if (q) TOUCH_PARAMS.forEach(function (k) { var v = q.get(k); if (v) parts.push(k + '=' + encodeURIComponent(v)); });
    // Marketing params present → let the SERVER stamp the durable cookie (first-touch-wins there).
    if (parts.length && marketingAllowed()) {
      try {
        parts.push('t=' + Date.now());   // cache-buster; the server uses its own timestamp
        var img = new Image();
        img.onload = stampAdTouch;       // cookie is set once the pixel response lands → copy to cart
        img.onerror = stampAdTouch;
        img.src = TRACK_PROXY + '?' + parts.join('&');
      } catch (e) {}
    }
    // Also copy any already-durable cookie from a previous visit (the whole point: a later
    // Google visit still carries the carousel _ad_first set weeks ago).
    stampAdTouch();
  })();

  // ── DEMAND-SIGNALS-THEME.md — the things we were blind to. Coarse values only, never PII
  //    (search terms are queries, occasion labels are UI text, dates go out as days-ahead). ──
  function onDomReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // Site search: the query on the results page (covers typed + predictive submits — they all land
  // on /search), and zero-result searches.
  onDomReady(function () {
    try {
      if (location.pathname.indexOf('/search') !== 0) return;
      var q = '';
      try { q = (new URLSearchParams(location.search).get('q') || '').trim().slice(0, 60); } catch (e) {}
      if (!q) return;
      onceFire('search_q', 'search', 'query', q);
      var grid = document.getElementById('product-grid');
      if (!grid || !grid.querySelector('.grid__item')) onceFire('search_z', 'search', 'zero', q);
    } catch (e) {}
  });

  // Out-of-stock demand: a sold-out PDP (disabled add button) — impression, once per page.
  onDomReady(function () {
    try {
      if (location.pathname.indexOf('/products/') !== 0) return;
      var btn = document.querySelector('button[name="add"][disabled], .bbfx-atc[disabled]');
      if (btn && /sold\s*out/i.test(btn.textContent || '')) {
        onceFire('oos', 'stock', 'oos', location.pathname.split('/').pop().slice(0, 60));
      }
    } catch (e) {}
  });

  // Occasion chosen: the personaliser / gifting occasion tiles — tile label only.
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('.mb-occ') : null;
    if (!t) return;
    var name = (t.getAttribute('data-occ') || t.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40);
    if (name) fire('occasion', 'selected', name);
  }, true);

  // Delivery date chosen (corporate flow's required-delivery-date input): DAYS AHEAD, never the date.
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.classList || !el.classList.contains('bb-date-input')) return;
    if (!el.value) return;
    var d = new Date(el.value);
    if (isNaN(d)) return;
    var today = new Date(); today.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
    fire('delivery', 'date', String(Math.round((d - today) / 86400000)));
  }, true);

  // AI story kept: builder submitted with the AI suggestion untouched (make-it-personal.liquid
  // sets __bbAIState 'unchanged' on generation and flips it to 'edited' on a real keystroke).
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.id !== 'bb-product-form') return;
    if (window.__bbAIState === 'unchanged') onceFire('ai_kept', 'personalise', 'ai_kept');
  }, true);

  // ── BEHAVIOUR-DEPTH-THEME.md (Group B) — filters, PDP expands, upsells, personaliser depth,
  //    free-shipping threshold. Coarse labels only, never any typed content. ──

  // Collection/search filters: name = the facet VALUE ticked; empty = 0 products after re-render.
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.name || String(el.name).indexOf('filter.') !== 0) return;
    if (el.type === 'checkbox' && !el.checked) return;   // only applying, not clearing
    var label = (el.value || el.name).toString().toLowerCase().slice(0, 40);
    fire('filter', 'used', label);
    setTimeout(function () {
      var grid = document.getElementById('product-grid');
      if (grid && !grid.querySelector('.grid__item')) fire('filter', 'empty', label);
    }, 1500);   // facets re-render via fetch; check after it settles
  }, true);

  // PDP/section accordions ('toggle' does not bubble, but capture still sees it).
  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (!d || d.tagName !== 'DETAILS' || !d.open) return;
    if (!d.closest('.product__accordion, .bb-accordions')) return;
    var t = d.querySelector('.accordion__title');
    var name = (t ? t.textContent : d.id || 'section').replace(/\s+/g, ' ').trim().toLowerCase().slice(0, 30);
    if (name) fire('view', 'expand', name);
  }, true);

  // Upsells: six-loops upgrade (builder) + gift card (cart). shown → accepted / declined.
  var ups = { 'six-loops': 0, 'gift-card': 0 };   // 0 = not shown, 1 = shown, 2 = resolved
  function upsellShown(key) { if (!ups[key]) { ups[key] = 1; fire('upsell', 'shown', key); } }
  function upsellResolve(key, name) { if (ups[key] === 1) { ups[key] = 2; fire('upsell', name, key); } }
  onDomReady(function () {
    try {
      if (!('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          upsellShown(en.target.getAttribute('data-bb-upsell'));
          io.unobserve(en.target);
        });
      }, { threshold: 0.5 });
      var six = document.querySelector('.mb-gift-six-btn');
      if (six) { six.setAttribute('data-bb-upsell', 'six-loops'); io.observe(six); }
      var gift = document.querySelector('[data-cart-gift]');
      if (gift) { gift.setAttribute('data-bb-upsell', 'gift-card'); io.observe(gift); }
    } catch (e) {}
  });
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.mb-gift-six-btn')) upsellResolve('six-loops', 'accepted');
  }, true);
  document.addEventListener('input', function (e) {
    var el = e.target;
    if (!el || !el.closest || !el.closest('[data-cart-gift]')) return;
    if ((el.value || '').trim()) upsellResolve('gift-card', 'accepted');
  }, true);

  // Personaliser depth: dwell (first touch → leave/submit), per-bar edits, furthest bar reached.
  var persoStart = 0, persoSent = false, lastBar = 0, barChanges = {};
  function barIndexOf(el) { return parseInt(el.getAttribute('data-bar') || el.getAttribute('data-chap') || '0', 10) || 0; }
  function isPersoField(el) { return el && el.classList && (el.classList.contains('mb-input') || el.classList.contains('mb-barfield') || el.classList.contains('mb-chap')); }
  document.addEventListener('input', function (e) {
    if (!isPersoField(e.target)) return;
    if (!persoStart) persoStart = Date.now();
    var bi = barIndexOf(e.target);
    if (bi > lastBar) lastBar = bi;
  }, true);
  document.addEventListener('change', function (e) {
    if (!isPersoField(e.target)) return;
    var bi = barIndexOf(e.target); if (!bi) return;
    barChanges[bi] = (barChanges[bi] || 0) + 1;
    if (barChanges[bi] > 1) fire('personalise', 'bar_edit', String(bi));   // change #2+ = an edit after first entry
  }, true);
  function sendPersoDepth() {
    if (persoSent || !persoStart) return;
    persoSent = true;
    fire('personalise', 'dwell', String(Math.round((Date.now() - persoStart) / 1000)));
    if (lastBar) fire('personalise', 'last_bar', String(lastBar));
  }
  document.addEventListener('submit', function (e) { if (e.target && e.target.id === 'bb-product-form') sendPersoDepth(); }, true);
  document.addEventListener('visibilitychange', function () { if (document.hidden) sendPersoDepth(); });

  // Free-shipping threshold hit (£30): compare this cart view's total against the last one we saw.
  onDomReady(function () {
    try {
      if (location.pathname.indexOf('/cart') !== 0) return;
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var total = (c && c.total_price) || 0;
          var prev = parseInt(sessionStorage.getItem('bb_cart_total_prev') || '0', 10);
          try { sessionStorage.setItem('bb_cart_total_prev', String(total)); } catch (e) {}
          if (prev > 0 && prev < 3000 && total >= 3000) fire('cart', 'shipping_threshold', 'hit');
        }).catch(function () {});
    } catch (e) {}
  });
})();
